import React, { useState, useRef, useCallback } from "react"
import { useDispatch } from "react-redux"

const COLORS = {
	bg: "#F5F3EF",
	surface: "#FFFFFF",
	border: "#E2DDD6",
	borderStrong: "#C8C0B4",
	text: "#1C1917",
	textMuted: "#78716C",
	textLight: "#A8A29E",
	markerR: "#DC2626",
	markerRLight: "#FEF2F2",
	markerRBorder: "#FECACA",
	markerE: "#2563EB",
	markerELight: "#EFF6FF",
	markerEBorder: "#BFDBFE",
	markerM: "#16A34A",
	markerMLight: "#F0FDF4",
	markerMBorder: "#BBF7D0",
	eraser: "#78716C",
	eraserLight: "#F5F3EF",
	eraserBorder: "#E2DDD6",
}

export const LOCATION_MARQUEUR_RAYE = "R"
export const LOCATION_MARQUEUR_ENFONCE = "E"
export const LOCATION_MARQUEUR_MANQUANT = "M"

const TOOLS = {
	[LOCATION_MARQUEUR_RAYE]: LOCATION_MARQUEUR_RAYE,
	[LOCATION_MARQUEUR_ENFONCE]: LOCATION_MARQUEUR_ENFONCE,
	[LOCATION_MARQUEUR_MANQUANT]: LOCATION_MARQUEUR_MANQUANT,
	ERASER: "ERASER",
}

const TOOL_CONFIG = {
	[LOCATION_MARQUEUR_RAYE]: {
		label: "Rayé",
		color: COLORS.markerR,
		bg: COLORS.markerRLight,
		border: COLORS.markerRBorder,
		activeBg: COLORS.markerR,
		activeColor: "#fff",
	},
	[LOCATION_MARQUEUR_ENFONCE]: {
		label: "Enfoncé",
		color: COLORS.markerE,
		bg: COLORS.markerELight,
		border: COLORS.markerEBorder,
		activeBg: COLORS.markerE,
		activeColor: "#fff",
	},
	[LOCATION_MARQUEUR_MANQUANT]: {
		label: "Manquant",
		color: COLORS.markerM,
		bg: COLORS.markerMLight,
		border: COLORS.markerMBorder,
		activeBg: COLORS.markerM,
		activeColor: "#fff",
	},
	[TOOLS.ERASER]: {
		label: "Gomme",
		color: COLORS.eraser,
		bg: COLORS.eraserLight,
		border: COLORS.eraserBorder,
		activeBg: COLORS.text,
		activeColor: "#fff",
	},
}

const MarkerDot = ({ marker, onErase, isEraser, idx }) => {
	const markerConfig = TOOL_CONFIG[marker.type]
	const color = markerConfig.color
	const bg = markerConfig.bg

	return (
		<div
			onClick={isEraser ? () => onErase(idx) : undefined}
			style={{
				position: "absolute",
				left: `${marker.x}%`,
				top: `${marker.y}%`,
				transform: "translate(-50%, -50%)",
				width: 28,
				height: 28,
				borderRadius: "50%",
				backgroundColor: bg,
				border: `2px solid ${color}`,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				cursor: isEraser ? "pointer" : "default",
				boxShadow: `0 2px 8px ${color}40`,
				transition: "transform 0.1s, box-shadow 0.1s",
				zIndex: 10,
				userSelect: "none",
			}}
			title={isEraser ? "Cliquer pour supprimer" : marker.type}
			onMouseEnter={(e) => {
				if (isEraser) {
					e.currentTarget.style.transform =
						"translate(-50%, -50%) scale(1.2)"
					e.currentTarget.style.boxShadow = `0 4px 12px ${COLORS.markerR}60`
				}
			}}
			onMouseLeave={(e) => {
				e.currentTarget.style.transform =
					"translate(-50%, -50%) scale(1)"
				e.currentTarget.style.boxShadow = `0 2px 8px ${color}40`
			}}
		>
			<span
				style={{
					fontSize: 13,
					fontWeight: 700,
					color,
					fontFamily: "'DM Sans', sans-serif",
					lineHeight: 1,
				}}
			>
				{marker.type}
			</span>
		</div>
	)
}

export default function ImageMarker({
	imageUrl = "",
	viewMarkers = "",
	markers = [],
	updateMarkers = (markers) => {},
	copyFromAvant = () => {},
	editable = true,
}) {
	const [activeTool, setActiveTool] = useState(LOCATION_MARQUEUR_RAYE)
	const containerRef = useRef(null)

	const handleImageClick = useCallback(
		(e) => {
			if (activeTool === TOOLS.ERASER || !editable) return

			const rect = containerRef.current.getBoundingClientRect()
			const x = ((e.clientX - rect.left) / rect.width) * 100
			const y = ((e.clientY - rect.top) / rect.height) * 100

			const newMarker = {
				id: null,
				type: activeTool,
				x: parseFloat(x.toFixed(2)),
				y: parseFloat(y.toFixed(2)),
				is_avant: viewMarkers === "avant",
			}
			updateMarkers([...markers, newMarker])
		},
		[activeTool, markers, updateMarkers, viewMarkers, editable],
	)

	const eraseMarker = useCallback(
		(idx) => {
			updateMarkers([...markers.slice(0, idx), ...markers.slice(idx + 1)])
		},
		[updateMarkers, markers],
	)

	return (
		<div
			style={{
				fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
				backgroundColor: COLORS.surface,
				borderRadius: 16,
				border: `1px solid ${COLORS.border}`,
				overflow: "hidden",
			}}
		>
			{/* Toolbar */}
			{editable && (
				<div
					style={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "space-between",
						flex: 1,
						padding: "10px 20px",
						borderBottom: `1px solid ${COLORS.border}`,
						alignItems: "center",
						gap: 8,
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
						}}
					>
						{Object.entries(TOOL_CONFIG).map(([tool, config]) => {
							const isActive = activeTool === tool
							return (
								<button
									key={tool}
									onClick={() => setActiveTool(tool)}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 6,
										padding: "6px 14px",
										borderRadius: 8,
										border: `1px solid ${isActive ? config.activeBg : config.border}`,
										backgroundColor: isActive
											? config.activeBg
											: config.bg,
										color: isActive
											? config.activeColor
											: config.color,
										fontSize: 13,
										fontWeight: 500,
										cursor: "pointer",
										fontFamily: "inherit",
										transition: "all 0.15s",
									}}
								>
									<span
										style={{
											fontWeight: 700,
											fontSize:
												tool === TOOLS.ERASER ? 14 : 13,
										}}
									>
										{tool === TOOLS.ERASER ? "⌫" : tool}
									</span>
									{config.label}
								</button>
							)
						})}
					</div>
					{viewMarkers !== "avant" && (
						<button
							onClick={copyFromAvant}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 6,
								height: 30,
								padding: "6px 14px",
								borderRadius: 8,
								border: `1px solid ${TOOL_CONFIG.ERASER.border}`,
								backgroundColor: TOOL_CONFIG.ERASER.bg,
								color: TOOL_CONFIG.ERASER.color,
								fontSize: 13,
								fontWeight: 500,
								cursor: "pointer",
								fontFamily: "inherit",
								transition: "all 0.15s",
							}}
						>
							Copier
						</button>
					)}
				</div>
			)}
			{/* Image */}
			<div
				ref={containerRef}
				onClick={handleImageClick}
				style={{
					position: "relative",
					width: "100%",
					paddingBottom: `${(560 / 800) * 100}%`,
					cursor:
						activeTool === TOOLS.ERASER ? "crosshair" : "crosshair",
					userSelect: "none",
					backgroundColor: "#1C1917",
				}}
			>
				<img
					src={imageUrl}
					alt="État véhicule"
					draggable={false}
					style={{
						position: "absolute",
						inset: 0,
						width: "100%",
						height: "100%",
						objectFit: "cover",
						display: "block",
						pointerEvents: "none",
					}}
				/>

				{markers.map((marker, idx) => (
					<MarkerDot
						key={`${viewMarkers}_${idx}`}
						marker={marker}
						onErase={eraseMarker}
						isEraser={activeTool === TOOLS.ERASER}
						idx={idx}
					/>
				))}
			</div>
		</div>
	)
}
